import { Injectable, inject } from '@angular/core';
import { ApiQueryParams, ApiService } from '@app/gateway';
import type { DashboardFullResponse, InvoiceQuery } from '../models';
import { InvoiceFactory, InvoiceModel } from '../models';

export interface InvoicesPaginatedResult {
  data: InvoiceModel[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface InvoicesFilters {
  fantasia?: string;
  dataInicio?: Date;
  dataFim?: Date;
  ativo?: boolean;
}

@Injectable({ providedIn: 'root' })
export class InvoicesService {
  private readonly apiService = inject(ApiService);

  public async getAllInvoices(params: { page: number; limit: number } & InvoicesFilters): Promise<InvoicesPaginatedResult> {
    try {
      const path: string = InvoiceFactory.getPath();
      const query: ApiQueryParams = { page: params.page, limit: params.limit };
      if (params.fantasia) query['fantasia'] = params.fantasia;
      if (params.dataInicio) query['dataInicio'] = params.dataInicio.toISOString();
      if (params.dataFim) query['dataFim'] = params.dataFim.toISOString();
      if (params.ativo !== undefined) query['ativo'] = params.ativo;
      return this.apiService.get<InvoicesPaginatedResult>({ path, query });
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async sendInvoiceByAccountId(accountId: string, periodoInicial: Date, periodoFinal: Date): Promise<void> {
    try {
      const path = `${InvoiceFactory.getPath()}/send`;
      const body = {
        accountIds: [accountId],
        periodoInicial: periodoInicial.toISOString(),
        periodoFinal: periodoFinal.toISOString(),
      };
      await this.apiService.post({ path, data: body });
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  public async invoiceActive(accountId: string, flag: boolean): Promise<void> {
    try {
      const path: string = `${InvoiceFactory.getPath()}/active`;
      const data = {
        accountId,
        flag,
      };
      await this.apiService.post({ path, data });
    } catch (e: any) {
      throw new Error(e.message, { cause: e });
    }
  }

  private formatDate(date?: Date): string | undefined {
    return date ? date.toISOString() : undefined;
  }

  private buildDashboardBody(query: InvoiceQuery): Record<string, unknown> {
    return {
      periodoInicial: this.formatDate(query.startDate),
      periodoFinal: this.formatDate(query.endDate),
    };
  }

  public async getDashboardFull(query: InvoiceQuery): Promise<DashboardFullResponse> {
    try {
      const path = `${InvoiceFactory.getPath()}/dashboard`;
      return this.apiService.post<DashboardFullResponse>({ path, data: this.buildDashboardBody(query) });
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : 'Unknown error';
      throw new Error(errorMessage, { cause: e });
    }
  }
}
